/*
appTime — the SPA runs on the API's clock, whatever the API says that is.

XOL-414 established the wire contract. XOL-448 / XOL-449 moved the clock itself
behind it: this module used to hold `KL_OFFSET = '+08:00'` as a source literal
while the API derived everything from `APP_TIMEZONE`. The two agreed only by
coincidence of two constants matching in two repositories, and repointing the
API would have shifted every rendered timestamp with nothing to signal it.

The API pins its MariaDB connection to the configured offset and serialises
every Eloquent date through `NormalizesDatesToAppTimezone`, so the wire
contract is:

  instant        `2026-08-17 09:00:00`   bare app-timezone wall clock, no offset
  calendar date  `2026-08-17`            zoneless

This module is the SPA's matching half. Everything that reads a date from the
API, does arithmetic on one, or sends one back goes through `toAppTime` /
`appNow`, so results no longer depend on the browser's timezone.

Two rules that are easy to get wrong and expensive to get wrong:

1. NEVER parse with `moment(value, FMT).utcOffset(offset, true)`. `keepLocalTime`
   resolves the digits in the *host* zone first, so a value landing in the
   browser's DST spring-forward gap is silently shifted and then faithfully
   preserved. Measured on moment 2.29.1:

     America/New_York   '2026-03-08 02:30:00' -> 03:30   (1h wrong)
     Asia/Beirut        '2026-03-29 00:30:00' -> 01:30   (1h wrong)
     America/Santiago   '2026-09-06 00:00:00' -> 01:00   (1h wrong)

   On a read-modify-write that corruption goes back to the database. Appending
   the offset to the string and using `moment.parseZone` is exact in every zone.

2. A date-semantic column must NEVER be serialised as an instant. `formatDateSql`
   emits `YYYY-MM-DD` and always will. `actions.completed_at` / `approved_at` /
   `actualDate` / `targetDate` are calendar dates; `action_steps.completed_at` /
   `approved_at` are instants. They share names across tables and must not be
   "harmonised".

WHERE THE OFFSET COMES FROM

`GET api/system/config` carries a `timezone` object — `{name, offset, next}` —
and `@/lib/appConfig` hands it to `setAppTimezone()` on both the fetch path and
the sessionStorage-hydration path. There is deliberately NO hardcoded default
here: a constant in this file is precisely the defect this replaced.

Before the config lands the browser's own offset is used.

BE PRECISE ABOUT THAT WINDOW — it is wider than the login screen. On a same-tab
reload it is genuinely tiny: `hydrateConfigFromSession()` restores the cached
`timezone` synchronously at main.js:60, before `bootEnvironment()` runs, so even
the module-eval `appNow()` already has the right clock. But sessionStorage is
PER TAB while the JWT lives in localStorage, so an authenticated boot with no
cached config is a real state:

  * a new tab — cmd-clicked link, pasted deep link;
  * private mode, or storage blocked (`safeSessionStorage()` returns null);
  * the first load after a session teardown's prefix sweep.

There the refetch is fired un-awaited from `init()` and swallows its own errors,
so the whole authenticated app renders on the browser clock until it lands, and
indefinitely if it fails.

What that can and cannot break: relabelling a bare wire string is
offset-invariant, so DISPLAY of API dates is unaffected. The exposure is
`appNow*` and instant conversions — a field defaulted to "today" and saved in
that window can write the wrong calendar day. A London user opening a link at
18:30 local sees `appNowDateSql()` return yesterday's date in +08:00 terms.

Narrowing it further is a UX decision (gate date-writing on
`isAppTimezoneConfigured()`, or block the first render on the config promise)
and is deliberately not taken here — but do not read this paragraph as an
argument that the window is harmless.

`moment-timezone` is still not installed and still not needed: this module
never resolves a zone name, it is handed a number. The API does that work,
where `DateTimeZone::getTransitions()` answers it exactly — see
`App\Support\AppTimezone`. `next` carries the upcoming DST change so a tab left
open across a transition corrects itself without a refetch.

Two limits, stated so they are not mistaken for oversights:

  * The offset describes NOW. In a DST zone, a bare wire string dated the other
    side of a transition is relabelled an hour out. Per-value offsets were
    considered and deliberately deferred; the effect is exactly nil while the
    configured zone is fixed-offset, as Asia/Kuala_Lumpur has been since 1982.
  * The payload names one transition. A session open past two would need a
    refetch, which any re-login already forces.

Babel 6: no optional chaining, no nullish coalescing, no `.finally()`.
------------------------------------------------------------------------------ */
import moment from 'moment'

import { registerResetHook } from '@/lib/session'

// Canonical wire formats. `@/components/Data` re-exports these so the ~84
// existing importers keep working against a single definition.
export const DATE_SQL_FORMAT = 'YYYY-MM-DD'
export const DATETIME_SQL_FORMAT = 'YYYY-MM-DD HH:mm:ss'

// Element UI `value-format` tokens — lower-case `yyyy`/`dd`, NOT moment's
// `YYYY`/`DD`. Getting this wrong sends the literal mask to the API. Setting
// value-format is what stops `el-date-picker` v-modelling a JS Date, and stops
// its no-value-format fallback from coercing `'2026-08-17'` through
// `new Date(...)` — which is UTC midnight, and so renders as the previous day
// for any browser west of Greenwich.
export const PICKER_DATE_FORMAT = 'yyyy-MM-dd'
export const PICKER_DATETIME_FORMAT = 'yyyy-MM-dd HH:mm:ss'
export const PICKER_MONTH_FORMAT = 'yyyy-MM'
export const PICKER_YEAR_FORMAT = 'yyyy'

/**
 * The Element `value-format` matching a picker `type`, so the picker emits a
 * wire-shaped string instead of a Date.
 */
export function pickerValueFormat (type) {
  if (type === 'year') {
    return PICKER_YEAR_FORMAT
  }
  if (type === 'month' || type === 'monthrange') {
    return PICKER_MONTH_FORMAT
  }
  if (type === 'datetime' || type === 'datetimerange') {
    return PICKER_DATETIME_FORMAT
  }
  return PICKER_DATE_FORMAT
}

// ─────────────────────────────────────────────────────────────────────────────
// The clock, as supplied by the API
// ─────────────────────────────────────────────────────────────────────────────

// Real UTC offsets only: hours 00-14 (Kiritimati is +14:00, the maximum), and
// minutes drawn from the four values tzdata actually uses. A looser
// `\d{2}:\d{2}` admits '+99:99', which moment does NOT reject — it resolves to
// 6039 minutes and silently shifts every date, which is the opposite of the
// "a broken clock must look broken" rule below.
const OFFSET_SHAPE = /^[+-](?:0\d|1[0-4]):(?:00|15|30|45)$/

// Null until `api/system/config` answers. Exported as functions rather than
// constants on purpose: an exported const is read once at import, so a
// destructuring importer would freeze the pre-config value for the life of the
// page.
let zoneName = null
let offset = null
let nextTransition = null

/**
 * Adopt the `timezone` object from the config payload.
 *
 * A malformed payload is refused rather than absorbed — there is no default to
 * fall back to, and a broken clock must look broken. A malformed `next` is
 * dropped on its own, so one bad transition cannot cost us a good `offset`.
 *
 * @param {{name: string, offset: string, next: ?{offset: string, atEpochMs: number}}} payload
 * @returns {boolean} whether the payload was accepted
 */
export function setAppTimezone (payload) {
  if (!payload || typeof payload !== 'object' ||
      typeof payload.name !== 'string' || payload.name === '' ||
      !OFFSET_SHAPE.test(payload.offset)) {
    console.error('appTime: unusable timezone in the config payload, keeping the current clock', payload)
    return false
  }

  zoneName = payload.name
  offset = payload.offset
  nextTransition = readTransition(payload.next)

  return true
}

/**
 * Back to the browser's clock. Registered as a session reset hook below: the
 * configured zone arrives with an authenticated config payload, so it must not
 * outlive the session that fetched it.
 */
export function resetAppTimezone () {
  zoneName = null
  offset = null
  nextTransition = null
}

/** Whether the API has supplied a clock yet. */
export function isAppTimezoneConfigured () {
  return offset !== null
}

/** The configured IANA zone name, or the browser's while unconfigured. */
export function appTimezone () {
  return zoneName === null ? browserZone() : zoneName
}

/**
 * The offset to stamp on values right now, as `±HH:MM`.
 *
 * Past a reported transition the successor offset applies, so a tab open across
 * a DST boundary stops being an hour out on its own.
 */
export function appOffset () {
  if (offset === null) {
    return browserOffset()
  }

  if (nextTransition !== null && Date.now() >= nextTransition.atEpochMs) {
    return nextTransition.offset
  }

  return offset
}

function readTransition (next) {
  // A fixed-offset zone reports `next: null`, which is the normal case and not
  // worth a word of complaint.
  if (!next || typeof next !== 'object') {
    return null
  }

  // `> 0` and not merely finite: an atEpochMs of 0 or a negative makes
  // `Date.now() >= atEpochMs` true forever, so the successor offset would apply
  // from the instant the payload landed and never stop.
  if (!OFFSET_SHAPE.test(next.offset) ||
      typeof next.atEpochMs !== 'number' || !isFinite(next.atEpochMs) || next.atEpochMs <= 0) {
    console.error('appTime: malformed DST transition ignored; the current offset still applies', next)
    return null
  }

  return { offset: next.offset, atEpochMs: next.atEpochMs }
}

/**
 * The host's current offset. `getTimezoneOffset()` counts minutes BEHIND UTC,
 * so the sign is inverted: +08:00 reports -480.
 */
function browserOffset () {
  return formatOffset(-new Date().getTimezoneOffset())
}

function browserZone () {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch (e) {
    // Old or exotic engine. The name is display-only; the offset above is what
    // the parsing actually depends on.
    return null
  }
}

function formatOffset (minutes) {
  const magnitude = Math.abs(minutes)

  return (minutes < 0 ? '-' : '+') +
    pad2(Math.floor(magnitude / 60)) + ':' + pad2(magnitude % 60)
}

function pad2 (n) {
  return (n < 10 ? '0' : '') + n
}

// The clock is handed over with an authenticated config payload, so it is
// session state. Registered here, beside the `let`s it clears, per the
// convention documented in `@/lib/session`.
registerResetHook('appTime', resetAppTimezone)

// ─────────────────────────────────────────────────────────────────────────────

// Legacy pre-XOL-414 wire shape: ISO-8601 carrying `Z` or `±HH:MM`. Still
// matched deliberately so this module is correct against both the old and new
// API, and stays correct for any column a future change misses.
// Anchored on a preceding time component so intent is unambiguous. A loose
// "ends with ±HHMM" also matches `01-01-2026` (the `-2026` tail), which landed
// in the same place via the fallback branch but read as luck rather than design.
const OFFSET_BEARING = /\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})$/
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const BARE_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/

/**
 * "Now", on the app clock, whatever the browser thinks.
 * Returns a moment — see `appNowDateSql` / `appNowDateTimeSql` for anything
 * headed for the wire, because `moment.prototype.toJSON` ignores `utcOffset`
 * and would re-emit UTC.
 */
export function appNow () {
  return moment().utcOffset(appOffset())
}

/**
 * Resolve any date-ish value onto the app clock.
 *
 * Instants (Date, moment, epoch ms, offset-bearing string) are CONVERTED — they
 * name a real point in time, so the app zone is just a different rendering of
 * it. Bare wire strings are RELABELLED — their digits already are app-zone wall
 * clock.
 *
 * @param {*} value
 * @returns {moment|null} a moment fixed at the app offset, or null when unusable
 */
export function toAppTime (value) {
  if (value === null || value === undefined) {
    return null
  }

  // Resolved once per call rather than per branch, so a single value cannot be
  // parsed against two different offsets if a transition lands mid-execution.
  const o = appOffset()

  if (moment.isMoment(value)) {
    return value.isValid() ? value.clone().utcOffset(o) : null
  }

  if (value instanceof Date || typeof value === 'number') {
    const inst = moment(value).utcOffset(o)
    return inst.isValid() ? inst : null
  }

  if (typeof value !== 'string') {
    return null
  }

  const s = value.trim()

  // 'Invalid date' is moment's own failure output; it reaches us when a
  // formatter result is fed back into another formatter.
  if (s === '' || s === 'Invalid date') {
    return null
  }

  let m
  if (OFFSET_BEARING.test(s)) {
    m = moment.parseZone(s).utcOffset(o)
  } else if (DATE_ONLY.test(s)) {
    m = moment.parseZone(s + 'T00:00:00' + o)
  } else if (BARE_DATETIME.test(s)) {
    m = moment.parseZone(s.replace(' ', 'T') + o)
  } else {
    // Unrecognised shape (a display format fed back in, say). Parse it as an
    // instant and convert, which is the best available guess.
    //
    // CAVEAT — this is the one path in this module that is still
    // browser-dependent: a non-ISO string falls through moment to `new Date(s)`,
    // which resolves in the host zone. It matches pre-XOL-414 behaviour, so it
    // is not a regression, but it is not a guarantee either. Nothing on the wire
    // should reach here; if something does, fix the producer rather than
    // widening this branch.
    m = moment.parseZone(s).utcOffset(o)
  }

  return m.isValid() ? m : null
}

/** Current app-zone calendar date as `YYYY-MM-DD`. Safe to put on the wire. */
export function appNowDateSql () {
  return appNow().format(DATE_SQL_FORMAT)
}

/** Current app-zone instant as `YYYY-MM-DD HH:mm:ss`. Safe to put on the wire. */
export function appNowDateTimeSql () {
  return appNow().format(DATETIME_SQL_FORMAT)
}

/**
 * Format a Date's *displayed* wall clock as `YYYY-MM-DD`, without converting.
 *
 * The deliberate exception to "a Date is an instant". Some widgets hand us a
 * Date whose fields are what the user is looking at rather than a real moment
 * in time — FullCalendar's visible-range bounds are local midnight of the
 * first/last cell it drew. Converting those to the app zone shifts both ends by
 * a day for a browser far enough east, which drops the last visible day's
 * events off the grid. Relabelling keeps the query aligned to the rendered grid.
 *
 * Use this ONLY for values that describe something already on screen. Anything
 * that names a real instant goes through `toAppTime`.
 */
export function toWallClockDateSql (value) {
  if (!(value instanceof Date) || isNaN(value.getTime())) {
    return null
  }
  return moment(value).format(DATE_SQL_FORMAT)
}

/**
 * True for the two leaf types that JSON.stringify would turn into a UTC ISO
 * string behind our back: a native Date (via `toJSON`) and a moment (whose
 * `toJSON` ignores `utcOffset`).
 */
export function isDateLike (value) {
  return (value instanceof Date) || moment.isMoment(value)
}

/**
 * Serialise any date-ish value as a bare app-zone instant for the wire.
 * Used by the axios safety net in `@/lib/axiosClient`.
 */
export function toAppDateTimeSql (value) {
  const m = toAppTime(value)
  return m === null ? null : m.format(DATETIME_SQL_FORMAT)
}
